import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useInAppFirst} from 'hooks/useInAppFirst'
import {getShowHowToCancelLink} from 'modules/searchApi/selectors'
import dynamic from 'next/dynamic'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {Brand} from '@vio/core/src/brand/types'
import {useGetBusinessUnitQuery} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {CustomFooter} from '@vio/shared/src/whiteLabel/CustomFooter'

import {FooterCopyrightSection} from './CopyrightSection/CopyrightSection'
import {FooterLinksSection} from './LinksSection/LinksSection'
import {FooterSocialSection} from './SocialSection/SocialSection'

const FooterGetTheAppSection = dynamic(
  () =>
    import('./GetTheAppSection/GetTheAppSection').then(mod => ({
      default: mod.FooterGetTheAppSection
    })),
  {ssr: false}
)

const TrustpilotWidget = dynamic(
  () =>
    import('@vio/shared/src/search/TrustpilotWidget').then(mod => ({
      default: mod.TrustpilotWidget
    })),
  {ssr: false}
)

const Wrapper = styled.div`
  color: white;
  background-color: ${cssTheme.colors.background.brand.inverse};
`

const FooterCopyrightWrapper = styled.div`
  color: white;
  border-top: 1px solid ${cssTheme.colors.border.brand.inverse};
  margin-top: ${cssTheme.layout.spacing.s800};
`

const StyledContentWrapper = styled.div`
  padding-bottom: ${cssTheme.layout.spacing.s600};
  max-width: 1120px;
  margin: auto;
`

const LayoutContainer = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s700};
  ${linariaMq.desktopSm} {
    display: grid;
    column-gap: ${cssTheme.layout.spacing.s600};
    flex-basis: 70%;
    grid-template-columns: repeat(3, 1fr);
    text-align: left;
  }
`

interface Props {
  brand: Brand
  linksSlot?: React.ReactNode
}

export const Footer = ({brand, linksSlot}: Props) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()

  const {isInAppFirst} = useInAppFirst()
  const {shell, retention} = useBrandCapabilities()
  const showHowToCancel = useSelector(getShowHowToCancelLink)
  const {data, isLoading} = useGetBusinessUnitQuery(undefined, {
    skip: !shell.hasTrustpilot
  })

  // InAppFirst mode (sc-185842) strips all site footer chrome in the partner's
  // in-app browser. Keep this after every hook call to preserve hook order.
  if (isInAppFirst) return null

  if (shell.hasCustomFooter)
    return <CustomFooter brand={brand} showHowToCancel={showHowToCancel} />

  const isSingleColumn = isMobile || isDesktopXs
  return (
    <Wrapper>
      <Box
        paddingTop="s900"
        paddingBottom="s900"
        paddingLeft="s600"
        paddingRight="s600"
      >
        <StyledContentWrapper>
          {linksSlot}

          <Box display="flex" justifyContent="space-between">
            <LayoutContainer>
              <FooterLinksSection brand={brand} />
              {isSingleColumn && shell.hasTrustpilot && (
                <Box display="flex" justifyContent="center">
                  <TrustpilotWidget
                    isLoading={isLoading}
                    variant="badge"
                    theme="dark"
                    numberOfReviews={data?.numberOfReviews?.total}
                    stars={data?.score?.stars}
                    trustScore={data?.score?.trustScore}
                  />
                </Box>
              )}
              {shell.showSocialMediaLinks && brand.socials && (
                <FooterSocialSection
                  brand={brand}
                  isSingleColumn={isSingleColumn}
                />
              )}
              {retention.hasGetTheApp && (
                <FooterGetTheAppSection isSingleColumn={isSingleColumn} />
              )}
            </LayoutContainer>
            {!isSingleColumn && shell.hasTrustpilot && (
              <TrustpilotWidget
                isLoading={isLoading}
                variant="card"
                theme="dark"
                numberOfReviews={data?.numberOfReviews?.total}
                stars={data?.score?.stars}
                trustScore={data?.score?.trustScore}
              />
            )}
          </Box>
          <Box marginTop="s800">
            <Divider />
            <FooterCopyrightWrapper>
              <FooterCopyrightSection brand={brand} />
            </FooterCopyrightWrapper>
          </Box>
        </StyledContentWrapper>
      </Box>
    </Wrapper>
  )
}
